import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card, Typography, Grid, Box, Button, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip,
    CircularProgress, Alert, IconButton, Menu, MenuItem, FormControlLabel,
    Checkbox, Tooltip, Badge, TextField, InputAdornment, Switch,
    FormControl, InputLabel, Select, Divider, Avatar, Dialog,
    DialogTitle, DialogContent, DialogActions, TablePagination
} from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import GetAppIcon from '@mui/icons-material/GetApp';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import WorkIcon from '@mui/icons-material/Work';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import { motion } from 'framer-motion';
import axios from 'axios';
import config from '../../config/config.json';
import { useNotify } from 'react-admin';

const host = config.production.host;

// Funzione per recuperare il token JWT
const getJwt = () => {
    return localStorage.getItem("feathers-jwt");
};

function PlayersViewPage() {
    const notify = useNotify();
    const navigate = useNavigate();
    const [players, setPlayers] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [fullscreen, setFullscreen] = React.useState(false);
    const [autoRefresh, setAutoRefresh] = React.useState(false);
    const [refreshInterval, setRefreshInterval] = React.useState(null);
    const [filterTeam, setFilterTeam] = React.useState('all');
    const [filterRuolo, setFilterRuolo] = React.useState('all');
    const [filterStatus, setFilterStatus] = React.useState('all');
    const [selectedPlayer, setSelectedPlayer] = React.useState(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [playerToDelete, setPlayerToDelete] = React.useState(null);

    // Paginazione
    const [page, setPage] = React.useState(0);
    const [limit, setLimit] = React.useState(25);
    const [total, setTotal] = React.useState(0);

    // Filtri per dropdown (caricati separatamente)
    const [teams, setTeams] = React.useState([]);
    const [ruoli, setRuoli] = React.useState([]);

    const [visibleColumns, setVisibleColumns] = React.useState(() => {
        const saved = localStorage.getItem('players-view-visible-columns');
        if (saved) {
            return JSON.parse(saved);
        }
        return ['foto', 'nomeGiocatore', 'nomeTeam', 'nomeRuolo', 'flVisibile', 'actions'];
    });

    const [anchorEl, setAnchorEl] = React.useState(null);
    const [sortConfig, setSortConfig] = React.useState({ key: 'nomeGiocatore', direction: 'asc' });

    // Handlers
    /* const handleViewPlayer = (player) => {
        setSelectedPlayer(player);
        setDialogOpen(true);
    }; */
    const handleViewPlayer = (player) => {
        navigate(`/giocatori/${player.playerId}`);
    };

    const handleEditPlayer = (player) => {
        navigate(`/giocatori/${player.playerId}/edit`)
        // notify(`(DISATTIVO) Modifica giocatore: ${player.nomeGiocatore}`, { type: 'info' });
    };

    const handleToggleVisibility = async (player) => {
        try {
            const jwt = getJwt();
            await axios.patch(
                `${host}/tbl-player/${player.playerId}`,
                { flVisibile: !player.flVisibile },
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            notify(`Giocatore ${player.flVisibile ? 'nascosto' : 'mostrato'}`, { type: 'success' });
            loadPlayers();
        } catch (error) {
            notify("Errore nell'aggiornamento", { type: 'error' });
        }
    };

    const handleDeleteClick = (player) => {
        setPlayerToDelete(player);
        setDeleteDialogOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!playerToDelete) return;

        try {
            const jwt = getJwt();
            await axios.delete(
                `${host}/tbl-player/${playerToDelete.playerId}`,
                { headers: { 'Authorization': `Bearer ${jwt}` } }
            );

            notify(`Giocatore ${playerToDelete.nomeGiocatore} eliminato con successo`, { type: 'success' });
            loadPlayers();
        } catch (error) {
            console.error("Errore nell'eliminazione giocatore:", error);
            notify("Errore nell'eliminazione del giocatore. Potrebbe avere dati correlati.", { type: 'error' });
        } finally {
            setDeleteDialogOpen(false);
            setPlayerToDelete(null);
        }
    };

    // Configurazione delle colonne
    const COLUMN_CONFIG = {
        foto: {
            label: 'Foto',
            key: 'foto',
            align: 'center',
            default: true,
            sortable: false,
            width: 80,
            render: (value, row) => (
                <Avatar
                    src={value ? `${host}/image-proxy-mw?url=${encodeURIComponent(value)}` : undefined}
                    alt={row.nomeGiocatore}
                    sx={{
                        width: 40,
                        height: 40,
                        margin: 'auto',
                        bgcolor: value ? 'transparent' : 'secondary.main'
                    }}
                >
                    {!value && row.nomeGiocatore ? row.nomeGiocatore.charAt(0).toUpperCase() : '?'}
                </Avatar>
            )
        },
        nomeGiocatore: {
            label: 'Nome Giocatore',
            key: 'nomeGiocatore',
            align: 'left',
            default: true,
            sortable: true,
            width: 200,
            render: (value, row) => (
                <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {value || 'Nome non disponibile'}
                    </Typography>
                    {row.note && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {row.note.length > 50 ? `${row.note.substring(0, 50)}...` : row.note}
                        </Typography>
                    )}
                </Box>
            )
        },
        nomeTeam: {
            label: 'Squadra',
            key: 'nomeTeam',
            align: 'center',
            default: true,
            sortable: true,
            width: 150,
            render: (value, row) => (
                <Box sx={{ textAlign: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 0.5 }}>
                        <Avatar
                            src={row.teamFoto ? `${host}/image-proxy-mw?url=${encodeURIComponent(row.teamFoto)}` : undefined}
                            sx={{ width: 20, height: 20 }}
                        >
                            {value?.charAt(0)}
                        </Avatar>
                        <Chip
                            label={value || `Team ${row.teamId}`}
                            size="small"
                            sx={{ bgcolor: '#e8f4fd', color: '#1565c0' }}
                        />
                    </Box>
                    {/* <Typography variant="caption" color="text.secondary">
                        ID: {row.teamId}
                    </Typography> */}
                </Box>
            )
        },
        nomeRuolo: {
            label: 'Ruolo',
            key: 'nomeRuolo',
            align: 'center',
            default: true,
            sortable: true,
            width: 120,
            render: (value, row) => (
                <Box sx={{ textAlign: 'center' }}>
                    <Chip
                        label={value || 'Ruolo N/A'}
                        size="small"
                        sx={{
                            bgcolor: '#fff3e0',
                            color: '#e65100',
                            fontWeight: 500,
                            mb: 0.5
                        }}
                        icon={<WorkIcon sx={{ fontSize: 16 }} />}
                    />
                    {/*  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        ID: {row.ruoloId}
                    </Typography> */}
                </Box>
            )
        },
        flVisibile: {
            label: 'Stato',
            key: 'flVisibile',
            align: 'center',
            default: true,
            sortable: true,
            width: 120,
            render: (value, row) => {
                let status = 'Attivo';
                let color = '#4caf50';
                let bgcolor = '#e8f5e8';

                if (row.flAnnullato) {
                    status = 'Annullato';
                    color = '#f44336';
                    bgcolor = '#ffebee';
                } else if (!row.flAttivo) {
                    status = 'Inattivo';
                    color = '#ff9800';
                    bgcolor = '#fff3e0';
                } else if (!row.flVisibile) {
                    status = 'Nascosto';
                    color = '#9e9e9e';
                    bgcolor = '#f5f5f5';
                }

                return (
                    <Chip
                        label={status}
                        size="small"
                        sx={{ color, bgcolor, fontWeight: 500 }}
                    />
                );
            }
        },
        ordinamento: {
            label: 'Ordine',
            key: 'ordinamento',
            align: 'center',
            default: false,
            sortable: true,
            width: 80,
            render: (value) => (
                <Typography variant="body2">
                    {value || '-'}
                </Typography>
            )
        },
        createdAt: {
            label: 'Creato il',
            key: 'createdAt',
            align: 'center',
            default: false,
            sortable: true,
            width: 120,
            render: (value) => (
                <Typography variant="body2">
                    {value ? new Date(value).toLocaleDateString('it-IT') : '-'}
                </Typography>
            )
        },
        updatedAt: {
            label: 'Aggiornato il',
            key: 'updatedAt',
            align: 'center',
            default: false,
            sortable: true,
            width: 120,
            render: (value) => (
                <Typography variant="body2">
                    {value ? new Date(value).toLocaleDateString('it-IT') : '-'}
                </Typography>
            )
        },
        actions: {
            label: 'Azioni',
            key: 'actions',
            align: 'center',
            default: true,
            sortable: false,
            width: 120,
            render: (value, row) => (
                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Visualizza dettagli">
                        <IconButton size="small" onClick={() => handleViewPlayer(row)}>
                            <InfoIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Modifica">
                        <IconButton size="small" onClick={() => handleEditPlayer(row)}>
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={row.flVisibile ? "Nascondi" : "Mostra"}>
                        <IconButton
                            size="small"
                            onClick={() => handleToggleVisibility(row)}
                            color={row.flVisibile ? "default" : "warning"}
                        >
                            {row.flVisibile ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Elimina">
                        <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(row)}
                            color="error"
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    };

    // Costruisce query FeathersJS
    const buildQuery = () => {
        const query = {
            $limit: limit,
            $skip: page * limit
        };

        // Ordinamento
        if (sortConfig.key && COLUMN_CONFIG[sortConfig.key]?.sortable) {
            query.$sort = {
                [sortConfig.key]: sortConfig.direction === 'asc' ? 1 : -1
            };
        }

        // Filtri
        const filters = {};

        // Ricerca testuale (operatore $or per cercare in più campi)
        if (searchTerm.trim()) {
            const search = `%${searchTerm.trim()}%`;
            filters.$or = [
                { nomeGiocatore: { $like: search } },
                { nomeTeam: { $like: search } },
                { nomeRuolo: { $like: search } },
                { note: { $like: search } }
            ];
        }

        // Filtro team
        if (filterTeam !== 'all') {
            filters.teamId = parseInt(filterTeam);
        }

        // Filtro ruolo
        if (filterRuolo !== 'all') {
            filters.ruoloId = parseInt(filterRuolo);
        }

        // Filtro status
        if (filterStatus !== 'all') {
            switch (filterStatus) {
                case 'active':
                    filters.flAttivo = true;
                    filters.flAnnullato = false;
                    break;
                case 'inactive':
                    filters.flAttivo = false;
                    filters.flAnnullato = false;
                    break;
                case 'hidden':
                    filters.flVisibile = false;
                    break;
                case 'cancelled':
                    filters.flAnnullato = true;
                    break;
            }
        }

        // Aggiungi filtri alla query se presenti
        if (Object.keys(filters).length > 0) {
            Object.assign(query, filters);
        }

        return query;
    };


    // Carica i giocatori con paginazione e filtri FeathersJS
    const loadPlayers = async () => {
        try {
            setLoading(true);
            setError(null);
            const jwt = getJwt();

            const query = buildQuery();

            const response = await axios.get(
                `${host}/v-player`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: query
                }
            );

            const data = response.data;
            setPlayers(data.data || []);
            setTotal(data.total || 0);

        } catch (error) {
            console.error("Errore nel recupero giocatori:", error);
            setError("Errore nel caricamento dei giocatori");
            notify("Errore nel caricamento dei giocatori", { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    // Carica teams per dropdown filtro
    const loadTeams = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(
                `${host}/tbl-team`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        $select: ['id', 'nome'],
                        flVisibile: 1,
                        flAttivo: 1,
                        $sort: { nome: 1 }
                    }
                }
            );
            setTeams(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento teams:", error);
        }
    };

    // Carica ruoli per dropdown filtro
    const loadRuoli = async () => {
        try {
            const jwt = getJwt();
            const response = await axios.get(
                `${host}/tbl-ruolo`,
                {
                    headers: { 'Authorization': `Bearer ${jwt}` },
                    params: {
                        $select: ['id', 'nome'],
                        $sort: { nome: 1 }
                    }
                }
            );
            setRuoli(response.data.data || []);
        } catch (error) {
            console.error("Errore nel caricamento ruoli:", error);
        }
    };

    // Altri handlers
    const handleColumnToggle = (columnKey) => {
        setVisibleColumns(prev => {
            if (prev.includes(columnKey)) {
                return prev.filter(key => key !== columnKey);
            } else {
                return [...prev, columnKey];
            }
        });
    };

    const handleSort = (columnKey) => {
        if (!COLUMN_CONFIG[columnKey]?.sortable) return;

        setSortConfig(prev => ({
            key: columnKey,
            direction: prev.key === columnKey && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };

    const handlePageChange = (event, newPage) => {
        setPage(newPage);
    };

    const handleLimitChange = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const resetFilters = () => {
        setSearchTerm('');
        setFilterTeam('all');
        setFilterRuolo('all');
        setFilterStatus('all');
        setPage(0);
    };

    const resetToDefault = () => {
        setVisibleColumns(['foto', 'nomeGiocatore', 'nomeTeam', 'nomeRuolo', 'flVisibile', 'actions']);
        setAnchorEl(null);
    };

    const exportToCSV = () => {
        const headers = visibleColumns
            .filter(key => key !== 'actions')
            .map(key => COLUMN_CONFIG[key].label)
            .join(',');

        const rows = players.map(player =>
            visibleColumns
                .filter(key => key !== 'actions')
                .map(key => {
                    let value = player[key] || '';
                    if (key === 'flVisibile') {
                        if (player.flAnnullato) value = 'Annullato';
                        else if (!player.flAttivo) value = 'Inattivo';
                        else if (!player.flVisibile) value = 'Nascosto';
                        else value = 'Attivo';
                    }
                    return `"${value}"`;
                }).join(',')
        ).join('\n');

        const csv = headers + '\n' + rows;
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `giocatori_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const toggleFullscreen = () => {
        setFullscreen(!fullscreen);
    };

    // Auto refresh
    React.useEffect(() => {
        if (autoRefresh) {
            const interval = setInterval(() => {
                loadPlayers();
            }, 30000);
            setRefreshInterval(interval);
        } else {
            if (refreshInterval) {
                clearInterval(refreshInterval);
                setRefreshInterval(null);
            }
        }

        return () => {
            if (refreshInterval) {
                clearInterval(refreshInterval);
            }
        };
    }, [autoRefresh]);

    // Carica dati quando cambiano filtri, paginazione o ordinamento
    React.useEffect(() => {
        loadPlayers();
    }, [page, limit, sortConfig, searchTerm, filterTeam, filterRuolo, filterStatus]);

    // Carica teams e ruoli al mount
    React.useEffect(() => {
        loadTeams();
        loadRuoli();
    }, []);

    // Salva configurazione colonne
    React.useEffect(() => {
        localStorage.setItem('players-view-visible-columns', JSON.stringify(visibleColumns));
    }, [visibleColumns]);

    // Calcola statistiche (solo sui dati correnti)
    const stats = {
        totali: total,
        attivi: players.filter(p => p.flAttivo && !p.flAnnullato).length,
        inattivi: players.filter(p => !p.flAttivo && !p.flAnnullato).length,
        nascosti: players.filter(p => !p.flVisibile).length,
        teams: [...new Set(players.map(p => p.teamId))].length,
        ruoli: [...new Set(players.map(p => p.ruoloId))].length
    };

    return (
        <Box sx={{
            p: fullscreen ? 1 : { xs: 2, md: 4 },
            bgcolor: '#f6f7fb',
            minHeight: '100vh',
            position: fullscreen ? 'fixed' : 'relative',
            top: fullscreen ? 0 : 'auto',
            left: fullscreen ? 0 : 'auto',
            right: fullscreen ? 0 : 'auto',
            bottom: fullscreen ? 0 : 'auto',
            zIndex: fullscreen ? 9999 : 'auto'
        }}>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>

                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                        👨‍💼 Gestione Giocatori
                    </Typography>
                    {/* <Typography variant="subtitle1" sx={{ color: 'grey.600' }}>
                        Vista completa con paginazione e filtri backend FeathersJS
                    </Typography> */}
                    <Card sx={{ p: 3, borderRadius: 3, boxShadow: 2, height: 'fit-content' }}>
                        <Button

                            fullWidth
                            variant="outlined"
                            startIcon={<AddCircleIcon />}
                            sx={{
                                mb: 2,
                                color: '#4A90E2',
                                borderColor: '#4A90E2',
                                fontWeight: 600,
                                borderRadius: 2,
                                py: 1.5,
                                '&:hover': {
                                    borderColor: '#357ABD',
                                    color: '#357ABD',
                                    bgcolor: 'rgba(74, 144, 226, 0.04)'
                                }
                            }}
                            onClick={() => navigate('/giocatori/create')}>
                            Nuova Giocatore
                        </Button>
                    </Card>
                </Box>

                {/* Controlli superiori */}
                <Card sx={{ p: 2, mb: 3, borderRadius: 3, boxShadow: 2 }}>
                    <Grid container spacing={2} alignItems="center">

                        {/* Ricerca */}
                        <Grid item xs={12} md={2.5}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Cerca giocatore, team, ruolo..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searchTerm && (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => setSearchTerm('')}
                                            >
                                                <ClearIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                            />
                        </Grid>

                        {/* Filtro Team */}
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Squadra</InputLabel>
                                <Select
                                    value={filterTeam}
                                    label="Squadra"
                                    onChange={(e) => {
                                        setFilterTeam(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">Tutte</MenuItem>
                                    {teams.map(team => (
                                        <MenuItem key={team.id} value={team.id}>
                                            {team.nome || `Team ${team.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filtro Ruolo */}
                        <Grid item xs={6} md={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Ruolo</InputLabel>
                                <Select
                                    value={filterRuolo}
                                    label="Ruolo"
                                    onChange={(e) => {
                                        setFilterRuolo(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    {ruoli.map(ruolo => (
                                        <MenuItem key={ruolo.id} value={ruolo.id}>
                                            {ruolo.nome || `Ruolo ${ruolo.id}`}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Filtro Status */}
                        <Grid item xs={6} md={1.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Stato</InputLabel>
                                <Select
                                    value={filterStatus}
                                    label="Stato"
                                    onChange={(e) => {
                                        setFilterStatus(e.target.value);
                                        setPage(0);
                                    }}
                                >
                                    <MenuItem value="all">Tutti</MenuItem>
                                    <MenuItem value="active">Attivi</MenuItem>
                                    <MenuItem value="inactive">Inattivi</MenuItem>
                                    <MenuItem value="hidden">Nascosti</MenuItem>
                                    <MenuItem value="cancelled">Annullati</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Auto refresh */}
                        <Grid item xs={6} md={1.5}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={autoRefresh}
                                        onChange={(e) => setAutoRefresh(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label="Auto refresh"
                                sx={{ margin: 0 }}
                            />
                        </Grid>

                        {/* Controlli */}
                        <Grid item xs={12} md={2.5}>
                            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>

                                <Tooltip title="Cancella filtri">
                                    <IconButton
                                        size="small"
                                        onClick={resetFilters}
                                        disabled={searchTerm === '' && filterTeam === 'all' && filterRuolo === 'all' && filterStatus === 'all'}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title="Esporta CSV">
                                    {<IconButton
                                        size="small"
                                        onClick={exportToCSV}
                                        disabled={!players.length}
                                    >
                                        <GetAppIcon />
                                    </IconButton>}
                                </Tooltip>

                                <Tooltip title="Configura colonne">
                                    <IconButton
                                        size="small"
                                        onClick={(e) => setAnchorEl(e.currentTarget)}
                                    >
                                        <Badge badgeContent={visibleColumns.length} color="primary">
                                            <SettingsIcon />
                                        </Badge>
                                    </IconButton>
                                </Tooltip>

                                <Tooltip title={fullscreen ? "Esci da schermo intero" : "Schermo intero"}>
                                    <IconButton
                                        size="small"
                                        onClick={toggleFullscreen}
                                    >
                                        {fullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                                    </IconButton>
                                </Tooltip>

                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                                    onClick={loadPlayers}
                                    disabled={loading}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Aggiorna
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </Card>

                {/* Menu configurazione colonne */}
                <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={() => setAnchorEl(null)}
                    PaperProps={{
                        sx: { maxHeight: 500, width: 280 }
                    }}
                >
                    <MenuItem onClick={resetToDefault}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Reset a default
                        </Typography>
                    </MenuItem>
                    <Divider />
                    {Object.entries(COLUMN_CONFIG).map(([key, config]) => (
                        <MenuItem key={key} onClick={() => handleColumnToggle(key)}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={visibleColumns.includes(key)}
                                        size="small"
                                    />
                                }
                                label={config.label}
                                sx={{ margin: 0, width: '100%' }}
                            />
                        </MenuItem>
                    ))}
                </Menu>

                {/* Statistiche rapide */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4A90E2' }}>
                                {stats.totali}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Inattivi (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#9e9e9e' }}>
                                {stats.nascosti}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Nascosti (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1565c0' }}>
                                {stats.teams}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Squadre (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Card sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#e65100' }}>
                                {stats.ruoli}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Ruoli (pagina)
                            </Typography>
                        </Card>
                    </Grid>
                </Grid>

                {/* Tabella principale */}
                <Card sx={{ borderRadius: 3, boxShadow: 2 }}>

                    {error && (
                        <Alert severity="error" sx={{ m: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && !players.length ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
                            <CircularProgress size={40} />
                        </Box>
                    ) : players.length > 0 ? (
                        <>
                            <TableContainer sx={{ maxHeight: fullscreen ? 'calc(100vh - 400px)' : 600 }}>
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            {visibleColumns.map(columnKey => {
                                                const config = COLUMN_CONFIG[columnKey];
                                                console.log(`Rendering column: ${columnKey}`, config);

                                                return (
                                                    <TableCell
                                                        key={columnKey}
                                                        align={config.align}
                                                        sx={{
                                                            fontWeight: 600,
                                                            width: config.width,
                                                            cursor: config.sortable ? 'pointer' : 'default',
                                                            bgcolor: sortConfig.key === columnKey ? 'rgba(25, 118, 210, 0.08)' : 'inherit',
                                                            '&:hover': config.sortable ? {
                                                                bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                            } : {},
                                                            borderBottom: '2px solid #e0e0e0'
                                                        }}
                                                        onClick={() => handleSort(columnKey)}
                                                    >
                                                        {config.label}
                                                        {sortConfig.key === columnKey && (
                                                            <span style={{ marginLeft: 4 }}>
                                                                {sortConfig.direction === 'asc' ? '↑' : '↓'}
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {players.map((player, index) => (
                                            <TableRow
                                                key={player.playerId}
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0, 0, 0, 0.04)'
                                                    },
                                                    bgcolor: index % 2 === 0 ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                                                    opacity: player.flAnnullato ? 0.6 : 1
                                                }}
                                            >
                                                {visibleColumns.map(columnKey => {
                                                    const config = COLUMN_CONFIG[columnKey];
                                                    const value = player[config.key];

                                                    return (
                                                        <TableCell
                                                            key={columnKey}
                                                            align={config.align}
                                                            sx={{
                                                                width: config.width,
                                                                py: 1
                                                            }}
                                                        >
                                                            {config.render ?
                                                                config.render(value, player, index) :
                                                                value || '-'
                                                            }
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Paginazione */}
                            <TablePagination
                                component="div"
                                count={total}
                                page={page}
                                onPageChange={handlePageChange}
                                rowsPerPage={limit}
                                onRowsPerPageChange={handleLimitChange}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                labelRowsPerPage="Righe per pagina:"
                                labelDisplayedRows={({ from, to, count }) =>
                                    `${from}-${to} di ${count !== -1 ? count : `più di ${to}`}`
                                }
                                sx={{
                                    borderTop: '1px solid #e0e0e0',
                                    '& .MuiTablePagination-toolbar': {
                                        px: 2
                                    }
                                }}
                            />
                        </>
                    ) : (
                        <Box sx={{ textAlign: 'center', p: 6 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {searchTerm || filterTeam !== 'all' || filterRuolo !== 'all' || filterStatus !== 'all' ?
                                    'Nessun giocatore trovato con i filtri applicati' :
                                    'Nessun giocatore presente'
                                }
                            </Typography>
                            {(searchTerm || filterTeam !== 'all' || filterRuolo !== 'all' || filterStatus !== 'all') && (
                                <Button
                                    variant="outlined"
                                    onClick={resetFilters}
                                    sx={{ mt: 2 }}
                                >
                                    Cancella filtri
                                </Button>
                            )}
                        </Box>
                    )}
                </Card>

                {/* Dialog dettagli giocatore */}
                <Dialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                            src={selectedPlayer?.foto ? `${host}/image-proxy-mw?url=${encodeURIComponent(selectedPlayer.foto)}` : undefined}
                            alt={selectedPlayer?.nomeGiocatore}
                            sx={{ width: 50, height: 50 }}
                        >
                            {selectedPlayer?.nomeGiocatore?.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                            <Typography variant="h6">
                                {selectedPlayer?.nomeGiocatore || 'Giocatore senza nome'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ID: {selectedPlayer?.playerId} • {selectedPlayer?.nomeTeam} • {selectedPlayer?.nomeRuolo}
                            </Typography>
                        </Box>
                    </DialogTitle>

                    <DialogContent>
                        {selectedPlayer && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Informazioni Giocatore
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Nome:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedPlayer.nomeGiocatore || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Player ID:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedPlayer.playerId || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Ordinamento:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedPlayer.ordinamento || 'Non impostato'}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Squadra
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Nome:</Typography>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Avatar
                                                        src={selectedPlayer.teamFoto ? `${host}/image-proxy-mw?url=${encodeURIComponent(selectedPlayer.teamFoto)}` : undefined}
                                                        sx={{ width: 20, height: 20 }}
                                                    >
                                                        {selectedPlayer.nomeTeam?.charAt(0)}
                                                    </Avatar>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {selectedPlayer.nomeTeam || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Team ID:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedPlayer.teamId}
                                                </Typography>
                                            </Box>
                                            {selectedPlayer.teamNote && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Note Team:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                        bgcolor: '#f5f5f5',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        display: 'block'
                                                    }}>
                                                        {selectedPlayer.teamNote}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Ruolo
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Nome:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedPlayer.nomeRuolo || 'N/A'}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Ruolo ID:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedPlayer.ruoloId}
                                                </Typography>
                                            </Box>
                                            {selectedPlayer.ruoloNote && (
                                                <Box>
                                                    <Typography variant="body2" color="text.secondary" gutterBottom>
                                                        Note Ruolo:
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                        bgcolor: '#f5f5f5',
                                                        p: 1,
                                                        borderRadius: 1,
                                                        display: 'block'
                                                    }}>
                                                        {selectedPlayer.ruoloNote}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Stato e Flags
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Visibile:</Typography>
                                                <Chip
                                                    label={selectedPlayer.flVisibile ? 'Sì' : 'No'}
                                                    size="small"
                                                    color={selectedPlayer.flVisibile ? 'success' : 'default'}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Attivo:</Typography>
                                                <Chip
                                                    label={selectedPlayer.flAttivo ? 'Sì' : 'No'}
                                                    size="small"
                                                    color={selectedPlayer.flAttivo ? 'success' : 'warning'}
                                                />
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="body2" color="text.secondary">Annullato:</Typography>
                                                <Chip
                                                    label={selectedPlayer.flAnnullato ? 'Sì' : 'No'}
                                                    size="small"
                                                    color={selectedPlayer.flAnnullato ? 'error' : 'success'}
                                                />
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                                <Grid item xs={12}>
                                    <Card sx={{ p: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            Timestamp
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Creato il:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedPlayer.createdAt ?
                                                        new Date(selectedPlayer.createdAt).toLocaleString('it-IT') :
                                                        'N/A'
                                                    }
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                                <Typography variant="body2" color="text.secondary">Aggiornato il:</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                    {selectedPlayer.updatedAt ?
                                                        new Date(selectedPlayer.updatedAt).toLocaleString('it-IT') :
                                                        'N/A'
                                                    }
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Card>
                                </Grid>

                                {selectedPlayer.note && (
                                    <Grid item xs={12}>
                                        <Card sx={{ p: 2 }}>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Note Giocatore
                                            </Typography>
                                            <Typography variant="body2" sx={{
                                                bgcolor: '#f5f5f5',
                                                p: 2,
                                                borderRadius: 1,
                                                fontStyle: 'italic'
                                            }}>
                                                {selectedPlayer.note}
                                            </Typography>
                                        </Card>
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </DialogContent>

                    <DialogActions>
                        <Button onClick={() => setDialogOpen(false)}>
                            Chiudi
                        </Button>
                        <Button
                            variant="contained"
                            onClick={() => {
                                handleEditPlayer(selectedPlayer);
                                setDialogOpen(false);
                            }}
                        >
                            Modifica
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Dialog conferma eliminazione */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={() => setDeleteDialogOpen(false)}
                >
                    <DialogTitle>Conferma Eliminazione</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Sei sicuro di voler eliminare definitivamente il giocatore <strong>{playerToDelete?.nomeGiocatore}</strong>?
                            Questa azione non può essere annullata.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setDeleteDialogOpen(false)}>Annulla</Button>
                        <Button onClick={handleConfirmDelete} color="error" variant="contained">
                            Elimina
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Footer con info */}
                {players.length > 0 && (
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            Ultimo aggiornamento: {new Date().toLocaleString('it-IT')}
                            {autoRefresh && " • Auto refresh attivo"}
                            • Pagina {page + 1} di {Math.ceil(total / limit)}
                            {(searchTerm || filterTeam !== 'all' || filterRuolo !== 'all' || filterStatus !== 'all') &&
                                ` • Filtri applicati`
                            }
                        </Typography>
                    </Box>
                )}

            </motion.div>
        </Box>
    );
}

export default PlayersViewPage;